import { reauth, reauthV2 } from 'common/services/auth.service';
import {
  redirectToProvider,
  debouncedToggleSnackBar
} from 'common/utils/utils';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';
import { Flex } from 'components/base/layout-components';
import Text from 'components/base/Text';
import * as TrackingService from 'common/services/tracking.service';
import { TRACKING, LOCAL_STORAGE_KEYS } from 'common/constants/constants';
import Fuse from 'fuse.js';
import _ from 'lodash';
import { formatRepository } from './repositoriesSections';
import { DefinitionLevels } from 'common/services/settings.service';
import AppRouter from 'common/AppRouter';

export const getSuccessSnackbarTitle = repositories => {
  const reposAdded =
    repositories.length === 1
      ? `Repository ${_.get(repositories[0], 'name', '')}`
      : `${repositories.length} Repositories`;

  return `${reposAdded} added successfully.`;
};

export const processingRepos = async ({
  setIsProcessingRepos,
  getRepositories
}) => {
  BrowserStorageAccessor().local.set(
    LOCAL_STORAGE_KEYS.IS_PROCESSING_REPOS,
    'true'
  );
  BrowserStorageAccessor().local.set(
    LOCAL_STORAGE_KEYS.FETCH_REPOS_TIMESTAMP,
    new Date().toISOString()
  );
  setIsProcessingRepos(true);

  if (_.isFunction(getRepositories)) {
    await getRepositories();
  }
};

export const onReauthorizeClick = ({
  id,
  sensor_name,
  gitStreamEnabled = false
}) => {
  const queryParams = {
    isOrgSettings: true,
    ...(window.location.search.includes('isE2eTestFlow')
      ? { isE2eTestFlow: 1 }
      : {}),
    sensor_auth_id: id,
    cacheCreds: true
  };

  redirectToProvider(
    gitStreamEnabled ? `${sensor_name}_gitstream` : sensor_name,
    {
      redirectPath: `${AppRouter.REPOSITORIES.path}?isOrgSettings=true${
        gitStreamEnabled ? '&gitstreamFlow=true' : ''
      }`,
      query: queryParams
    }
  );
};

export const onApproveReauth = async (
  { reauthParams, sensor_authorization_id },
  getSensorAuthorizations,
  getRepositories,
  sensor_name,
  isGitstreamFlow = false
) => {
  TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
    page: TRACKING.PAGES.SETTINGS,
    page_tab: TRACKING.PAGE_TABS.REPOS,
    click_type: TRACKING.CLICK.REAUTH_APPROVED
  });
  await reauth({
    cacheId: reauthParams.cacheId,
    providerName: sensor_name,
    sensor_auth_id: sensor_authorization_id,
    isGitstreamFlow
  });
  getSensorAuthorizations({ withGitStreamEnabled: true });
  getRepositories();
};

export const onApproveReauthV2 = async (
  { reauthParams, sensor_authorization_id },
  getSensorAuthorizations,
  sensor_name,
  isGitstreamFlow
) => {
  TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
    page: TRACKING.PAGES.SETTINGS,
    page_tab: TRACKING.PAGE_TABS.REPOS,
    click_type: TRACKING.CLICK.REAUTH_V2_APPROVED
  });
  await reauthV2({
    cacheId: reauthParams.cacheId,
    providerName: sensor_name,
    sensor_auth_id: sensor_authorization_id,
    isGitstreamFlow
  });
  getSensorAuthorizations({ withGitStreamEnabled: true });
};

export const onDeleteIntegrationClick = async ({
  toggleSnackBar,
  toggleModal,
  createConfirmModalOptions,
  name,
  deleteSensorAuthorization,
  getSensorAuthorizations,
  getRepositories,
  id
}) => {
  toggleModal(
    createConfirmModalOptions({
      title: `Delete Git integration ${name}?`,
      description: `Deleting this intrgration means We will not show any new data from the repositories on it`,
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      onConfirm: async () => {
        TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
          page: TRACKING.PAGES.SETTINGS,
          page_tab: TRACKING.PAGE_TABS.REPOS,
          click_type: TRACKING.CLICK.DELETE_INTEGRATION
        });
        try {
          await deleteSensorAuthorization(id, {
            soft_delete: true,
            with_repositories: true
          });
          await getSensorAuthorizations({ withGitStreamEnabled: true });
          await getRepositories();
          toggleSnackBar({
            message: (
              <Flex direction='column'>
                <Text size='large' color='white'>
                  {name} integration deleted successfully
                </Text>
              </Flex>
            ),
            variant: 'success'
          });
          toggleModal();
        } catch (e) {}
      },
      onCancel: () => toggleModal()
    })
  );
};

const handleRepoDeleteClick = async ({
  repository,
  deleteRepository,
  debouncedToggleSnackBar,
  toggleSnackBar
}) => {
  try {
    await deleteRepository(repository.id);
    debouncedToggleSnackBar(
      `Repository ${repository.name} was removed successfully.`,
      'NOTE: It might take couple of hours to see updated data reflecting this change.',
      true,
      toggleSnackBar
    );
  } catch (e) {
    debouncedToggleSnackBar(
      `Failed to delete ${repository.name}`,
      '',
      false,
      toggleSnackBar
    );
  }
};

const handleRepoSettingsClick = async ({
  pickedRepoName,
  pickedRepoId,
  getSettingsByDefinitionLevel,
  setRepoSettings,
  setPickedRepoName,
  setPickedRepoId,
  setIsSettingsOpen
}) => {
  TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
    page: TRACKING.PAGES.SETTINGS,
    page_tab: TRACKING.PAGE_TABS.REPOS,
    click_type: TRACKING.CLICK.REPO
  });

  const settings = getSettingsByDefinitionLevel(
    DefinitionLevels.REPOSITORY,
    pickedRepoId
  );
  setRepoSettings(
    _.orderBy(settings, 'viewOrder').filter(setting => setting.isVisible) || []
  );
  setPickedRepoName(pickedRepoName);
  setPickedRepoId(pickedRepoId);
  setIsSettingsOpen(true);
};

export const getTableData = ({
  repositories,
  term,
  updateRepository,
  saving,
  setSaving,
  setRepoSettings,
  getSettingsByDefinitionLevel,
  setPickedRepoName,
  setPickedRepoId,
  setIsSettingsOpen,
  deleteRepository,
  toggleSnackBar,
  getSensorAuthorizations,
  getRepositories,
  sensorAuthorizations,
  readonly,
  selectionProps
}) => {
  const options = {
    shouldSort: true,
    threshold: 0.2,
    location: 0,
    distance: 100,
    maxPatternLength: 32,
    keys: ['name']
  };

  const fuse = new Fuse(repositories, options);

  if (term) {
    repositories = fuse.search(term);
  }

  if (!sensorAuthorizations.length) {
    return [];
  }

  return _.chain(repositories)
    .orderBy(['name'], ['created_at', 'desc'])
    .map(repository =>
      formatRepository({
        repository,
        updateRepository,
        debouncedToggleSnackBar,
        handleRepoSettingsClick: (pickedRepoName, pickedRepoId) =>
          handleRepoSettingsClick({
            pickedRepoName,
            pickedRepoId,
            getSettingsByDefinitionLevel,
            setRepoSettings,
            setPickedRepoName,
            setPickedRepoId,
            setIsSettingsOpen
          }),
        saving,
        setSaving,
        handleRepoDeleteClick: repository =>
          handleRepoDeleteClick({
            repository,
            deleteRepository,
            debouncedToggleSnackBar,
            toggleSnackBar
          }),
        toggleSnackBar,
        getSensorAuthorizations,
        getRepositories,
        sensorAuthorizations,
        readonly,
        selectionProps
      })
    )
    .value();
};
